import { useCallback, useEffect, useState } from 'react';
import { CandidateStandard, Filters } from '../types';

interface UseCandidateSelectionParams {
  candidates: CandidateStandard[];
  filters: Filters;
}

/**
 * Keyed on tag_id, since a candidate has no model_tags row yet, and with no select-all: the Add panel
 * deliberately has no master checkbox, unlike the table above it.
 */
export default function useCandidateSelection({ candidates, filters }: UseCandidateSelectionParams) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const selected = candidates.filter(candidate => selectedIds.has(candidate.tag_id));

  // Stable identity keeps React.memo on CandidateRow effective while the user types.
  const toggleSelect = useCallback((tagId: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // A filter or search change drops the selection rather than leaving candidates ticked off-screen.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters]);

  // Added standards leave the list, and so do ones somebody else correlated. A stale id renders as
  // nothing at all but would still be counted by the bulk bar.
  useEffect(() => {
    setSelectedIds((current) => {
      const present = new Set(candidates.map(({ tag_id: tagId }) => tagId));
      const next = new Set([...current].filter(id => present.has(id)));

      return next.size === current.size ? current : next;
    });
  }, [candidates]);

  return { clearSelection, selected, selectedIds, toggleSelect };
}
